import * as React from 'react';

/* ─────────────────────────────────────────────
 * XYQAR Admin UI-Kit — unicorn-grade primitives
 * ──────────────────────────────────────────── */

export function PageTitle({ kicker, title, right }: { kicker: string; title: string; right?: React.ReactNode }) {
  return (
    <div className="mb-8 flex items-end justify-between gap-6">
      <div>
        <p className="text-[10px] tracking-[0.2em] text-white/40 uppercase font-mono">{kicker}</p>
        <h1 className="text-3xl sm:text-4xl font-normal text-white mt-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{title}</h1>
      </div>
      {right}
    </div>
  );
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-md bg-white/5 border border-white/10 p-6 ${className}`}>{children}</div>;
}

export function Btn({ children, onClick, primary, disabled, as, href }:
  { children: React.ReactNode; onClick?: () => void; primary?: boolean; disabled?: boolean; as?: 'a'; href?: string }) {
  const cls = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold tracking-[0.15em] uppercase transition ' +
    (primary ? 'bg-white text-black hover:opacity-90' : 'bg-transparent text-white/70 border border-white/20 hover:border-white/50') +
    (disabled ? ' opacity-50 cursor-not-allowed' : '');
  if (as === 'a' && href) return <a href={href} target="_blank" rel="noreferrer noopener" className={cls}>{children}</a>;
  return <button onClick={onClick} disabled={disabled} className={cls}>{children}</button>;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return <Card className="text-center py-16"><p className="text-white/60 text-sm">{title}</p>{hint && <p className="text-white/30 text-xs mt-2">{hint}</p>}</Card>;
}

/* ─── Pill: backwards compatible (kind=) + new (tone=) ─── */
type PillKind = 'gray' | 'green' | 'red' | 'amber';
type PillTone = 'mute' | 'live' | 'warn' | 'alert';
const TONE_CLS: Record<PillTone, string> = {
  mute:  'bg-white/5        text-white/60    border-white/10',
  live:  'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  warn:  'bg-amber-500/10   text-amber-300   border-amber-500/30',
  alert: 'bg-rose-500/10    text-rose-300    border-rose-500/30',
};
const KIND_TO_TONE: Record<PillKind, PillTone> = { gray: 'mute', green: 'live', red: 'alert', amber: 'warn' };

export function Pill({ children, kind, tone }: { children: React.ReactNode; kind?: PillKind; tone?: PillTone }) {
  const t = tone ?? (kind ? KIND_TO_TONE[kind] : 'mute');
  return <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-[0.15em] ${TONE_CLS[t]}`}>{children}</span>;
}

/* ─── KPI tile ─── */
export function Kpi({ label, value, hint, accent }:
  { label: string; value: React.ReactNode; hint?: React.ReactNode; accent?: 'green' | 'amber' | 'red' | 'blue' }) {
  const dot = accent === 'green' ? 'bg-emerald-400' :
              accent === 'amber' ? 'bg-amber-400'   :
              accent === 'red'   ? 'bg-rose-400'    :
              accent === 'blue'  ? 'bg-sky-400'     : 'bg-white/40';
  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</p>
      </div>
      <p className="text-3xl text-white mt-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</p>
      {hint && <p className="text-[11px] text-white/40 mt-2 font-mono">{hint}</p>}
    </Card>
  );
}

/* ─── Fairness pill ─── */
const FAIRNESS_COLORS: Record<string, string> = {
  GREEN:  'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  YELLOW: 'bg-amber-500/15  text-amber-300  border-amber-500/40',
  ORANGE: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  RED:    'bg-rose-500/15   text-rose-300   border-rose-500/40',
};
export function FairnessPill({ grade }: { grade?: string }) {
  const g = (grade || 'YELLOW').toUpperCase();
  const cls = FAIRNESS_COLORS[g] ?? FAIRNESS_COLORS.YELLOW;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono uppercase tracking-[0.15em] ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />{g}
    </span>
  );
}

/* ─── Distribution donut bars (fairness) ─── */
export function DistributionBar({ data }: { data: Array<{ k: string; v: number; tone?: PillTone }> }) {
  const total = data.reduce((s, x) => s + (x.v || 0), 0) || 1;
  return (
    <div>
      <div className="h-2 rounded-full overflow-hidden flex border border-white/10 bg-white/5">
        {data.map((d, i) => {
          const cls =
            d.tone === 'live'  ? 'bg-emerald-400' :
            d.tone === 'warn'  ? 'bg-amber-400'   :
            d.tone === 'alert' ? 'bg-rose-400'    :
            d.k === 'GREEN'    ? 'bg-emerald-400' :
            d.k === 'YELLOW'   ? 'bg-amber-400'   :
            d.k === 'ORANGE'   ? 'bg-orange-400'  :
            d.k === 'RED'      ? 'bg-rose-400'    : 'bg-white/30';
          const pct = (d.v / total) * 100;
          return <div key={i} className={cls} style={{ width: `${pct}%` }} title={`${d.k}: ${d.v.toLocaleString('fr-FR')}`} />;
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        {data.map((d, i) => {
          const pct = ((d.v / total) * 100).toFixed(1);
          return (
            <span key={i} className="text-[11px] text-white/60 font-mono">
              <span className="opacity-70">{d.k}</span>{' '}
              <span className="text-white">{d.v.toLocaleString('fr-FR')}</span>{' '}
              <span className="opacity-50">({pct}%)</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Table primitives ─── */
export function Table({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-white/10 overflow-hidden bg-white/5"><table className="w-full text-sm">{children}</table></div>;
}
export function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th className={`text-left text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono px-4 py-3 border-b border-white/10 ${className}`}>{children}</th>;
}
export function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-white/80 align-middle ${className}`}>{children}</td>;
}

/* ─── Section header ─── */
export function Section({ title, hint, right, children }: { title: string; hint?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-sm uppercase tracking-[0.2em] text-white/80 font-mono">{title}</h2>
          {hint && <p className="text-[11px] text-white/40 mt-1">{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export const fmt = (n: number | null | undefined, suffix = '') =>
  (n ?? 0).toLocaleString('fr-FR') + suffix;
